import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Hook para detectar e aplicar idioma baseado na localização geográfica
 * 
 * Mapeia países para idiomas:
 * - Países de língua espanhola -> 'es'
 * - Países de língua inglesa -> 'en'
 * - Brasil e Portugal -> 'pt'
 * 
 * @param {Object} location - Objeto com dados de localização (city, country_code, country_name)
 * @param {boolean} autoDetect - Se true, detecta e aplica automaticamente (default: true)
 * @returns {string} - Código do idioma detectado
 */
export function useLanguageDetection(location = null, autoDetect = true) {
  const { i18n } = useTranslation();

  useEffect(() => {
    // Se autoDetect está desabilitado, não faz nada
    if (!autoDetect) return;

    // Aguardar geolocalização carregar
    if (!location) {
      return;
    }

    const detectedLanguage = detectLanguageFromLocation(location);
    
    console.log('🌍 País detectado:', location.country_code, '→ Idioma:', detectedLanguage);
    
    // Só muda se for diferente do idioma atual
    if (detectedLanguage && i18n.language !== detectedLanguage) {
      console.log('🔄 Mudando idioma de', i18n.language, 'para', detectedLanguage);
      i18n.changeLanguage(detectedLanguage);
    }
    // NÃO salvar no localStorage — isso causava bug cross-session:
    // visita anterior (BR) salvava 'pt', próxima visita com VPN (US) carregava 'pt' do cache
    // → geoloc corrigia para 'en' mas já tinha renderizado conteúdo misturado
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, autoDetect]); // Remover i18n das dependências para evitar loop infinito

  return location ? detectLanguageFromLocation(location) : 'pt';
}

/**
 * Detecta idioma baseado na localização geográfica
 * @param {Object} location - Dados de localização (country_code, country_name)
 * @returns {string} - Código do idioma ('pt', 'es', 'en')
 */
export function detectLanguageFromLocation(location) {
  if (!location) return 'pt'; // Fallback para português

  const countryCode = location.country_code?.toLowerCase();

  // Países de língua portuguesa
  const portugueseCountries = ['br', 'pt', 'ao', 'mz', 'cv', 'gw', 'st', 'tl'];
  if (portugueseCountries.includes(countryCode)) {
    return 'pt';
  }

  // Países de língua espanhola
  const spanishCountries = [
    'es', // Espanha
    'mx', 'ar', 'co', 've', 'pe', 'cl', 'ec', 'gt', 'cu', 'bo', // América Latina
    'do', 'hn', 'py', 'sv', 'ni', 'cr', 'pa', 'uy', 'gq' // Mais países
  ];
  if (spanishCountries.includes(countryCode)) {
    return 'es';
  }

  // Países de língua inglesa
  const englishCountries = [
    'us', 'gb', 'ca', 'au', 'nz', 'ie', 'za', 'in', 'sg', 'ph'
  ];
  if (englishCountries.includes(countryCode)) {
    return 'en';
  }

  // Fallback: usar inglês como padrão internacional
  return 'en';
}

/**
 * Hook para obter a função de formatação de números baseada no idioma atual
 * @returns {Function} Função de formatação que aceita um número e retorna string formatada
 */
export function useNumberFormat() {
  const { i18n } = useTranslation();
  
  const localeMap = {
    pt: 'pt-BR',
    es: 'es-ES',
    en: 'en-US'
  };

  const locale = localeMap[i18n.language] || 'pt-BR';

  return (number) => {
    if (typeof number !== 'number') return number;
    return number.toLocaleString(locale);
  };
}

/**
 * Hook para obter a função de formatação de datas baseada no idioma atual
 * @returns {Function} Função de formatação que aceita uma data e retorna string formatada
 */
export function useDateFormat() {
  const { i18n } = useTranslation();
  
  const localeMap = {
    pt: 'pt-BR',
    es: 'es-ES',
    en: 'en-US'
  };

  const locale = localeMap[i18n.language] || 'pt-BR';

  return (date, options = { day: 'numeric', month: 'long', year: 'numeric' }) => {
    if (!(date instanceof Date)) date = new Date(date);
    return date.toLocaleDateString(locale, options);
  };
}
